import {useRouter} from 'next/router';
import {MouseEvent, ReactNode, useCallback, useMemo} from 'react';
import {openCashCharge} from '../../../../cashcharge/openCashCharge.function';
import {TotalBalance} from '../../../../common/containers/CashHistoryContainer/TotalBalance/TotalBalance';
import {t} from '../../../../common/getTranslations';
import {openWindow} from '../../../../common/openWindow.function';
import {requestContext} from '../../../../common/requestContext';
import {formatWithCommasAndDefaultHyphen} from '../../../../common/shared/utils/formatWithCommasAndDefaultHyphen';
import {Divider} from '../../../../design-system/atoms/Divider/Divider';
import {Icon} from '../../../../design-system/atoms/Icon/Icon';
import {IconType} from '../../../../design-system/atoms/Icon/IconType.enum';
import {TabPositionControl} from '../../../../design-system/atoms/TabPositionControl/TabPositionControl';
import {Text} from '../../../../design-system/atoms/Text/Text';
import {CashHistoryLineBanner} from '../../../../design-system/organisms/CashHistoryLineBanner/CashHistoryLineBanner';
import {
  CASH_TAB_ITEMS,
  WARNING_REMAIN_DAYS,
} from '../../../../design-system/organisms/HistoryItem/constants';
import {HistoryItem} from '../../../../design-system/organisms/HistoryItem/HistoryItem';
import {MainTab} from '../../../../design-system/organisms/MainTab/MainTab';
import {
  History,
  HistoryType,
  Maybe,
  UserCashDetail,
} from '../../../../graphql/generated/types';
import {LOG_CLICK_EVENT} from '../../../../logging/logEvent';
import {ReactQueryInfiniteType} from '../../../../reactQuery/CodegenReactQueryType.type';
import {ReactQueryInfiniteFetcher} from '../../../../reactQuery/ReactQueryInfiniteFetcher';
import {theme} from '../../../../theme';
import {
  useInfiniteBuyCashHistoryQuery,
  useInfiniteUseCashHistoryQuery,
  useUserCashQuery,
} from '../../../../wrapper/storeWrapper';
import {generateRelayRedirectUrl} from '../../../relay/redirect/util/generateRelayRedirectUrl';
import {HistoryListType} from '../../type';

export function CashHistoryContainer() {
  const router = useRouter();
  const currentType = (router.query?.tab as string) ?? HistoryListType.Buy;
  const {data: buyCashHistoryData} = useInfiniteBuyCashHistoryQuery(
    {
      page: 0,
    },
    {
      initialPageParam: {page: 0},
      getNextPageParam: () => undefined,
    }
  );
  const banner = buyCashHistoryData?.pages[0].buyCashHistory?.banner;

  const {data: userCashData} = useUserCashQuery(undefined, {
    refetchOnMount: true,
  });
  const cash = userCashData?.userCash;

  const {isMobile, isWebView} = requestContext.requestInfo.display;
  const isGlobal = requestContext.requestInfo.isGlobal;

  const handleClickTab = useCallback(
    (clickedTabHistoryId: HistoryListType) => {
      router.push(`/history/cash?tab=${clickedTabHistoryId}`);
    },
    [router]
  ) as (clickedTabHistoryId: string | number) => void;

  const totalBalance = useMemo(
    () =>
      formatWithCommasAndDefaultHyphen(
        {
          totalBalance: cash?.detail?.totalBalance || 0,
          eventSum: cash?.detail?.eventSum || 0,
          paidSum: cash?.detail?.paidSum || 0,
        },
        (param: UserCashDetail) => param.totalBalance || 0
      ),
    [cash]
  );
  const eventSum = useMemo(
    () =>
      formatWithCommasAndDefaultHyphen(
        {
          totalBalance: cash?.detail?.totalBalance || 0,
          eventSum: cash?.detail?.eventSum || 0,
          paidSum: cash?.detail?.paidSum || 0,
        },
        (param: UserCashDetail) => param.eventSum || 0
      ),
    [cash]
  );
  const paidSum = useMemo(
    () =>
      formatWithCommasAndDefaultHyphen(
        {
          totalBalance: cash?.detail?.totalBalance || 0,
          eventSum: cash?.detail?.eventSum || 0,
          paidSum: cash?.detail?.paidSum || 0,
        },
        (param: UserCashDetail) => param.paidSum || 0
      ),
    [cash]
  );

  const onClickCash = useCallback((e: MouseEvent) => {
    LOG_CLICK_EVENT('캐시충전_클릭', e, () =>
      openCashCharge({
        returnUrl: generateRelayRedirectUrl(window.location.href),
        cashChargeInfo: {
          cash_cvr: '캐시내역',
        },
      })
    );
  }, []);

  const onClickCashReceiptsResult = useCallback(
    (creditMasterId: Maybe<string> = '0') => {
      if (isMobile) {
        router.push(`/billing/cashreceipts-result?id=${creditMasterId}`);
      } else {
        openWindow({
          url: `/billing/cashreceipts-result?id=${creditMasterId}`,
          target: 'cashreceipts',
          features: 'width=360, height=680',
        });
      }
    },
    [isMobile, router]
  );

  const expiredCredit = cash?.expiredCredit || undefined;

  const CashHistoryFetcherComponent = useMemo(
    () =>
      ({children}: {children: (historyList?: History[]) => ReactNode}) =>
        currentType === HistoryListType.Buy ? (
          <ReactQueryInfiniteFetcher
            reactQuery={useInfiniteBuyCashHistoryQuery}
            type={ReactQueryInfiniteType.InfinityScroll}
            variables={{
              page: 0,
            }}
            onSuccess={data => {
              const lastPage = data.pages[data.pages.length - 1];
              /**
               * API에서 first_page가 true로 내려온다면 빌링 쪽 redis cache가 만료(7일)되어 정상적인 페이징이 불가능한 상황입니다.
               * 만료 시간이 길어 자주 발생할 케이스가 아니므로 플래그를 만나면 페이지를 리로드 시켜줍니다.
               */
              if (lastPage.buyCashHistory?.paging?.shouldReloadPage) {
                window.location.reload();
              }
            }}
            options={{
              refetchOnMount: true,
              initialPageParam: {page: 0},
              getNextPageParam: (lastPage, pages) => {
                return lastPage.buyCashHistory?.paging?.lastPageId
                  ? {
                      page: pages.length,
                      lastPageId: lastPage.buyCashHistory.paging.lastPageId,
                    }
                  : undefined;
              },
            }}
          >
            {data =>
              children(
                data?.pages.reduce(
                  (reducedHistoryList, page) =>
                    reducedHistoryList.concat(page.buyCashHistory?.list || []),
                  [] as History[]
                )
              )
            }
          </ReactQueryInfiniteFetcher>
        ) : (
          <ReactQueryInfiniteFetcher
            reactQuery={useInfiniteUseCashHistoryQuery}
            type={ReactQueryInfiniteType.InfinityScroll}
            variables={{
              page: 0,
            }}
            options={{
              refetchOnMount: true,
              initialPageParam: {page: 0},
              getNextPageParam: (lastPage, pages) => {
                return lastPage.useCashHistory?.isEnd
                  ? undefined
                  : {
                      page: pages.length,
                    };
              },
            }}
          >
            {data =>
              children(
                data?.pages.reduce(
                  (reducedHistoryList, page) =>
                    reducedHistoryList.concat(page.useCashHistory?.list || []),
                  [] as History[]
                )
              )
            }
          </ReactQueryInfiniteFetcher>
        ),
    [currentType]
  );

  return (
    <>
      {isMobile && (
        <>
          <TotalBalance
            isMobile={isMobile}
            isGlobal={isGlobal}
            totalBalance={totalBalance}
            eventSum={eventSum}
            paidSum={paidSum}
            expiredCredit={expiredCredit}
          />
          {!isGlobal && <CashHistoryLineBanner banner={banner} />}
        </>
      )}
      <TabPositionControl
        depth={1}
        isMobile={isMobile}
        isWebView={isWebView}
        disabled={!isMobile}
      >
        <div className="flex w-full flex-row justify-between">
          <MainTab
            showBorder
            isMobile={isMobile}
            title={t('cash_history_title') as string}
            currentTabItemId={currentType}
            tabItems={CASH_TAB_ITEMS}
            onClickTab={handleClickTab}
            className={!isMobile ? 'px-15pxr' : undefined}
          />
          {!isMobile && (
            <div className="mb-32pxr flex shrink-0 flex-row items-end">
              <div
                className="flex cursor-pointer rounded-full border-1 border-theme-transparent-20 pl-6pxr"
                onClick={onClickCash}
              >
                <Icon
                  type={IconType.ButtonCashFriendsStatic}
                  disableActiveEffect
                />
                <Text className="font-small2-bold my-8pxr mr-10pxr ml-2pxr text-theme-solid-100">
                  {t('cash_history_add_cash')}
                </Text>
              </div>
            </div>
          )}
        </div>
      </TabPositionControl>
      {!isMobile && (
        <>
          <TotalBalance
            isMobile={isMobile}
            totalBalance={totalBalance}
            eventSum={eventSum}
            paidSum={paidSum}
            isGlobal={isGlobal}
            expiredCredit={expiredCredit}
          />
          {!isGlobal && <CashHistoryLineBanner banner={banner} />}
        </>
      )}
      <CashHistoryFetcherComponent>
        {(historyList?: History[]) =>
          historyList && historyList.length > 0 ? (
            historyList.map((history: History, idx: number) => (
              <div key={`${history.id}-${idx}`}>
                <HistoryItem
                  history={history}
                  hasLink={
                    history.historyType === HistoryType.CouponUsage ||
                    history.historyType === HistoryType.CouponReCharge
                  }
                  hasCreditMasterId={!!history.creditMasterId}
                  onClickCashReceiptsResult={onClickCashReceiptsResult}
                  shouldWarnExpired={
                    (!!history.remainDays || history.remainDays === 0) &&
                    history.remainDays < WARNING_REMAIN_DAYS
                  }
                />
                <Divider
                  color={theme.colors['theme-transparent-10']}
                  className="mx-15pxr"
                />
              </div>
            ))
          ) : (
            <div className="my-101pxr mx-0 w-full text-center">
              <Text className="font-medium1 text-theme-solid-100">
                {currentType === HistoryListType.Buy
                  ? t('cash_history_no_purchase')
                  : t('cash_history_no_usage')}
              </Text>
            </div>
          )
        }
      </CashHistoryFetcherComponent>
    </>
  );
}
